import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const sellerboard: Competitor = {
  "id": "sellerboard",
  "name": "Sellerboard",
  "group": "ecomm",
  "website": "https://sellerboard.com",
  "owner": "Independent; operated by nextechnology GmbH (Stuttgart, Germany) per its Shopify App Store listing. No acquisition or funding news found; changelog shows active releases through October 2026.",
  "summary": "Profit analytics and automation suite built mainly for Amazon sellers, covering fees, PPC, inventory restock, reimbursement claims and repricing, with Walmart and eBay dashboards added. Shopify is served by a separate product (sellerboard.io) with its own plans.",
  "bestFor": [
    "Amazon-first brands (FBA/FBM) that want accurate per-SKU profit including every Amazon fee, refund and VAT line at low cost",
    "Amazon sellers who want PPC bid automation, restock planning, purchase orders and repricing in the same tool as their P&L",
    "Sellers also on Walmart and eBay who want one summary view across those marketplaces",
    "Agencies managing several Amazon client accounts (logo branding and per-account user rights)"
  ],
  "notFor": [
    "Shopify-led DTC brands that need a single consolidated P&L across Shopify and Amazon (Shopify runs as a separate product with its own subscription and dashboards)",
    "Brands that need TikTok Shop profit reporting inside the Amazon product (not listed there)",
    "Brands with wholesale, retail POS or ERP (e.g. NetSuite) revenue that must sit in the same model",
    "Teams that want their modeled data in their own warehouse or queryable with SQL"
  ],
  "strengths": [
    "Pulls Amazon settlement-level fees, refunds, storage, AWD and VAT detail automatically via SP-API, which a custom build would have to map and maintain itself",
    "Finds lost/damaged FBA units and under-reimbursed units (paid below COGS) over a 60-day window and generates claim reports and message templates",
    "Amazon PPC bid automation with target-ACOS strategies, keyword harvesting, negative-keyword filtering and (Oct 2026) day parting, which a reporting warehouse does not do",
    "Inventory planner with reorder recommendations from sales velocity, lead times and supplier settings, plus purchase orders, FBA shipments and reorder alerts",
    "Profit-aware Amazon repricer (Sept 2026) included in the subscription",
    "Walmart dashboard covering Walmart fees, WFS inventory and Walmart Connect plus Walmart SEM ad spend",
    "Very low cost: $15-$63/month billed annually for up to 50,000 orders/month, with a one-month free trial and no credit card",
    "COGS by period, batch, FIFO or weighted average, with automatic daily COGS import from a Google Sheet or CSV/Excel URL",
    "iOS and Android mobile apps, daily performance emails and alerts"
  ],
  "limitations": [
    "Shopify support is a separate product (sellerboard.io) with its own pricing; the July 2026 Summary Dashboard covers Amazon, Walmart and eBay only, and a third-party review describes separate dashboards per channel",
    "TikTok Shop is not mentioned on sellerboard.com, the FAQ or the 2025-2026 changelog; it appears only as an integration on the Shopify app listing",
    "Data leaves the product as XLSX/CSV report downloads; no public API, warehouse connector or SQL access found in vendor docs (its help docs suggest loading exports into Looker Studio or Power BI). The MCP connector is read-only",
    "Custom dashboards let you choose from existing metrics (up to 8 per tile); no user-defined formulas found",
    "LTV is per-product repeat-purchase metrics; no cohort or retention-rate analysis described in the help docs",
    "Accounts are capped at 30,000 products; Walmart support is limited to the US, Canada and Mexico",
    "Reimbursement claims must be filed manually by the seller in Seller Central, one case at a time",
    "PPC automation, repricer and inventory planner are documented for Amazon; not confirmed for Walmart, eBay or Shopify",
    "Reviewers mention a dated interface, a learning curve and support by email only (stated 24-hour response) (third-party: Capterra)"
  ],
  "pricing": {
    "model": "order volume (orders per month), with seller-account and user limits per tier",
    "publicStartingPrice": "$15/month billed annually ($19 billed monthly) for Amazon/Walmart/eBay, up to 3,000 orders/month; Shopify product from $9/month (600 orders)",
    "freeTier": "no - one-month free trial, no credit card; demo account available",
    "notes": "sellerboard.com (Amazon, Walmart, eBay), annual / monthly billing: Standard $15/$19, 3,000 orders/month, 4 seller accounts, 1 user; Professional $23/$29, 6,000 orders, 6 accounts, 2 users; Business $31/$39, 15,000 orders, 8 accounts, 4 users; Enterprise $63/$79, 50,000 orders, 16 accounts, 4 users, plus user access rights by account/marketplace/product and automated reports. A 'Need more?' option above Enterprise has no published price. Follow-up/review-request email quotas also rise by tier (150 to 30,000/month). Main cost drivers: monthly order volume, then number of seller accounts and users. Repricer is stated as included in the subscription; no add-on prices found. Shopify (sellerboard.io, separate subscription) billed monthly: Standard $9 (600 orders), Professional $19 (1,800), Business $39 (5,400), Enterprise $79 (unlimited). EU prices net of VAT. Setup: no vendor-stated setup time found; reviewers say entering historical COGS and fixed costs is the time-consuming one-off step (third-party: Capterra).",
    "sourceUrl": "https://sellerboard.com/walmart",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "shopify": {
      "value": "partial",
      "note": "Separate product (sellerboard.io) with its own plans and dashboards; not in the cross-marketplace Summary Dashboard",
      "sourceUrl": "https://sellerboard.io/",
      "badge": "Separate product"
    },
    "amazon": {
      "value": "yes",
      "note": "Core product; all Amazon marketplaces",
      "sourceUrl": "https://sellerboard.com/"
    },
    "tiktok_shop": {
      "value": "partial",
      "note": "Listed as an integration only on the Shopify app (sellerboard.io); not mentioned for the Amazon/Walmart product",
      "sourceUrl": "https://apps.shopify.com/sellerboard-profit-ltv-customer-lifetime-value-analytics"
    },
    "wholesale_retail_or_erp": {
      "value": "partial",
      "note": "Walmart and eBay marketplaces; QuickBooks and ShipStation mentioned; Shopify app lists ShipStation, Flexport, Veeqo. No NetSuite, ERP or retail POS found",
      "sourceUrl": "https://sellerboard.com/en/faq"
    },
    "ad_platforms": {
      "value": "partial",
      "note": "Amazon Ads and Walmart Connect/SEM in main product; Meta, Google, TikTok, Snapchat, Pinterest ads only in the Shopify product",
      "sourceUrl": "https://sellerboard.io/"
    },
    "first_party_pixel_attribution": {
      "value": "partial",
      "note": "Shopify product uses UTMs, first-party cookies and Shopify journey data; no multi-touch model described. Amazon product has Amazon Attribution for external traffic",
      "sourceUrl": "https://sellerboard.io/"
    },
    "mmm_incrementality": {
      "value": "unknown",
      "note": "Not mentioned in vendor pages checked",
      "sourceUrl": "https://sellerboard.com/"
    },
    "profit_cogs": {
      "value": "yes",
      "note": "Per-SKU profit with Amazon/Walmart fees, shipping, VAT, indirect expenses; COGS by period, batch, FIFO",
      "sourceUrl": "https://sellerboard.com/en/faq"
    },
    "ltv_cohorts": {
      "value": "partial",
      "note": "Per-product LTV (sales/orders per buyer over 3-48 months) and repeat-customer report; no cohort analysis described",
      "sourceUrl": "https://help.sellerboard.com/2-1-6-ltv/"
    },
    "subscription_analytics": {
      "value": "unknown",
      "note": "No Recharge or Subscribe & Save analytics found",
      "sourceUrl": "https://help.sellerboard.com/2-1-6-ltv/"
    },
    "warehouse_access": {
      "value": "unknown",
      "note": "No API or warehouse connector found; XLSX/CSV report exports only",
      "sourceUrl": "https://help.sellerboard.com/2-1-8-reports/"
    },
    "custom_metrics": {
      "value": "partial",
      "note": "Custom dashboard tiles pick from existing metrics (up to 8 per tile); no custom formulas found",
      "sourceUrl": "https://blog.sellerboard.com/2026/03/13/new-in-sellerboard-visual-pl-insights-custom-dashboards-stronger-profit-accuracy/"
    },
    "custom_sql": {
      "value": "unknown",
      "note": "SQL not mentioned in vendor docs checked",
      "sourceUrl": "https://help.sellerboard.com/2-1-8-reports/"
    },
    "ai_assistant": {
      "value": "partial",
      "note": "Read-only MCP connector for Claude, ChatGPT and other MCP agents; no built-in assistant found",
      "sourceUrl": "https://sellerboard.com/mcp"
    },
    "mobile_app": {
      "value": "yes",
      "note": "iOS and Android apps",
      "sourceUrl": "https://sellerboard.com/en/faq"
    }
  },
  "recentChanges": [
    {
      "date": "2025-08",
      "change": "Walmart Profit Dashboard launched (fees, ad costs, COGS by period, shipping profiles)",
      "sourceUrl": "https://blog.sellerboard.com/2025/08/03/%F0%9F%93%8A-walmart-sellers-meet-your-new-profit-dashboard/"
    },
    {
      "date": "2026-03",
      "change": "Custom dashboard tiles, P&L heatmap, COGS alerts, separated VAT, eBay order-level cost upload; Walmart inventory shown in Walmart dashboard",
      "sourceUrl": "https://blog.sellerboard.com/2026/03/13/new-in-sellerboard-visual-pl-insights-custom-dashboards-stronger-profit-accuracy/"
    },
    {
      "date": "2026-05",
      "change": "Walmart SEM ad spend added to Walmart profit metrics; ASP column; Walmart in daily emails",
      "sourceUrl": "https://blog.sellerboard.com/2026/05/21/asp-tracking-full-walmart-ad-spend-visibility-smarter-shipping-logic-whats-new-in-sellerboard/"
    },
    {
      "date": "2026-07",
      "change": "Summary Dashboard extended from Amazon-only to Amazon, Walmart and eBay",
      "sourceUrl": "https://blog.sellerboard.com/2026/07/16/new-in-sellerboard-summary-dashboard-for-amazon-walmart-and-ebay/"
    },
    {
      "date": "2026-08",
      "change": "Amazon Attribution page, automatic daily COGS import from Sheets/CSV URL, Real ACOS on Trends; Walmart multi-channel order handling and Walmart/eBay report exports",
      "sourceUrl": "https://blog.sellerboard.com/2026/08/13/new-in-sellerboard-amazon-attribution-automatic-cogs-import-and-real-acos-on-the-trends-page/"
    },
    {
      "date": "2026-09",
      "change": "Profit-based Amazon Repricer launched, stated as included in subscription",
      "sourceUrl": "https://blog.sellerboard.com/2026/09/03/the-sellerboard-repricer-dynamic-amazon-repricing-that-runs-on-your-real-profit-numbers/"
    },
    {
      "date": "2026-10",
      "change": "PPC day parting, COGS import by SKU/EAN/UPC, Amazon Attribution campaign profit, FBM refund reasons",
      "sourceUrl": "https://blog.sellerboard.com/2026/10/08/new-in-sellerboard-day-parting-easier-cogs-imports-and-more/"
    }
  ],
  "sources": [
    {
      "title": "sellerboard homepage and pricing",
      "url": "https://sellerboard.com/"
    },
    {
      "title": "sellerboard for Walmart (pricing table with account/user limits)",
      "url": "https://sellerboard.com/walmart"
    },
    {
      "title": "sellerboard FAQ",
      "url": "https://sellerboard.com/en/faq"
    },
    {
      "title": "sellerboard PPC",
      "url": "https://sellerboard.com/en/ppc"
    },
    {
      "title": "sellerboard inventory",
      "url": "https://sellerboard.com/inventory"
    },
    {
      "title": "sellerboard agencies",
      "url": "https://sellerboard.com/agencies"
    },
    {
      "title": "sellerboard MCP connector",
      "url": "https://sellerboard.com/mcp"
    },
    {
      "title": "sellerboard for Shopify (sellerboard.io)",
      "url": "https://sellerboard.io/"
    },
    {
      "title": "Sellerboard Profit Analytics on Shopify App Store",
      "url": "https://apps.shopify.com/sellerboard-profit-ltv-customer-lifetime-value-analytics"
    },
    {
      "title": "Help: Reports",
      "url": "https://help.sellerboard.com/2-1-8-reports/"
    },
    {
      "title": "Help: LTV",
      "url": "https://help.sellerboard.com/2-1-6-ltv/"
    },
    {
      "title": "Help: Lost & damaged inventory",
      "url": "https://help.sellerboard.com/2-5-lost-damaged-inventory/"
    },
    {
      "title": "Help: Reimbursement gap",
      "url": "https://help.sellerboard.com/2-5-4-reimbursement-gap/"
    },
    {
      "title": "Changelog archive",
      "url": "https://blog.sellerboard.com/category/changelog/"
    },
    {
      "title": "Changelog: Summary Dashboard for Amazon, Walmart and eBay (Jul 2026)",
      "url": "https://blog.sellerboard.com/2026/07/16/new-in-sellerboard-summary-dashboard-for-amazon-walmart-and-ebay/"
    },
    {
      "title": "Changelog: Walmart multi-channel handling and exports (Aug 2026)",
      "url": "https://blog.sellerboard.com/2026/08/17/new-in-sellerboard-cleaner-walmart-multi-channel-handling-a-walmart-only-summary-dashboard-and-report-exports-for-walmart-and-ebay/"
    },
    {
      "title": "Changelog: Repricer (Sep 2026)",
      "url": "https://blog.sellerboard.com/2026/09/03/the-sellerboard-repricer-dynamic-amazon-repricing-that-runs-on-your-real-profit-numbers/"
    },
    {
      "title": "Changelog: Day Parting and more (Oct 2026)",
      "url": "https://blog.sellerboard.com/2026/10/08/new-in-sellerboard-day-parting-easier-cogs-imports-and-more/"
    },
    {
      "title": "Revenue Geeks: Does Sellerboard work with Shopify? (third-party, Sept 2026)",
      "url": "https://revenuegeeks.com/sellerboard-shopify/"
    },
    {
      "title": "Capterra: sellerboard reviews (third-party)",
      "url": "https://www.capterra.com/p/238895/sellerboard/reviews/"
    }
  ],
  "lastReviewed": "2026-11-03"
};
